import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isMobileApp } from "../lib/platform";

const GoogleIcon = () => (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.7 6.4 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.6 0 19.5-8 19.5-19.5 0-1.2-.1-2.3-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.7 6.4 29.1 4.5 24 4.5 16.3 4.5 9.7 8.7 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.1-7.1 2.1-5.3 0-9.7-3.4-11.3-8L6.3 32.5C9.6 38.7 16.2 43.5 24 43.5z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.1-2.1 3.9-3.9 5.4l6.1 5c4.3-4 6.5-9.9 6.5-16.4 0-1.2-.1-2.3-.4-3.5z" />
    </svg>
);

export default function Signup() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/register", { name, email, password });
            setUser(data);
            // Inside the Android shell, send to /unlock (IAP). On the web,
            // route to /pricing — user picks Free viewer, 14-day trial, or
            // direct subscription (no more auto-redirect to Stripe).
            if (isMobileApp()) {
                navigate("/unlock", { replace: true });
                return;
            }
            navigate("/pricing", { replace: true });
        } catch (e) {
            // Surface the real backend message so users can see "Email already registered"
            // instead of a generic "Signup failed".
            const detail = e.response?.data?.detail;
            const msg = formatApiErrorDetail(detail);
            const status = e.response?.status;
            if (status === 409 || /already|in use|registered/i.test(msg)) {
                setError(`${msg || "That email is already registered."} → Try signing in instead.`);
            } else if (status === 400 && /password/i.test(msg)) {
                setError(msg);
            } else if (e.message === "Network Error") {
                setError("Couldn't reach the server. Please check your internet and try again.");
            } else {
                setError(msg || "Signup failed. Please try again.");
            }
            setLoading(false);
        }
    };

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/app";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <>
            <Helmet><title>Sign up — OpenCSV</title></Helmet>
            <section className="border-b border-black/10 min-h-[80vh] grid md:grid-cols-2">
                <div className="p-8 md:p-16 flex items-center order-2 md:order-1">
                    <div className="w-full max-w-sm mx-auto">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ create account</div>
                        <h2 className="font-display font-black tracking-tighter text-3xl mb-6">Create your account.</h2>

                        {!isMobileApp() && (
                            <>
                                <button onClick={handleGoogle} className="btn-brutal w-full inline-flex items-center justify-center gap-3 border border-ink/20 px-4 py-3 bg-white ui-heading text-sm font-semibold mb-4" data-testid="signup-google-button">
                                    <GoogleIcon /> Continue with Google
                                </button>
                                <div className="flex items-center gap-3 mb-4 text-[11px] uppercase tracking-widest text-ink-muted">
                                    <div className="flex-1 h-px bg-ink/10" /> or with email <div className="flex-1 h-px bg-ink/10" />
                                </div>
                            </>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-3" data-testid="signup-form">
                            <div>
                                <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">Name</label>
                                <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 bg-white focus-cobalt" data-testid="signup-name-input" />
                            </div>
                            <div>
                                <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">Email</label>
                                <input
                                    type="email"
                                    required
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    autoComplete="email"
                                    inputMode="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.trim())}
                                    className="mt-1 w-full border border-ink/20 px-3 py-2.5 bg-white focus-cobalt"
                                    data-testid="signup-email-input"
                                />
                            </div>
                            <div>
                                <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">Password (6+ chars)</label>
                                <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 bg-white focus-cobalt" data-testid="signup-password-input" />
                            </div>
                            {error && <div className="text-sm text-signal-red border border-signal-red/30 bg-signal-red/5 px-3 py-2" data-testid="signup-error">{error}</div>}
                            <button type="submit" disabled={loading} className="btn-brutal w-full bg-cobalt hover:bg-cobalt-hover text-white px-4 py-3 ui-heading font-semibold border border-cobalt disabled:opacity-60" data-testid="signup-submit-button">
                                {loading ? "Creating account…" : "Create free account →"}
                            </button>
                        </form>
                        <p className="mt-5 text-sm text-ink-soft">
                            Already have an account? <Link to="/login" className="text-cobalt hover:underline" data-testid="signup-login-link">Sign in</Link>
                        </p>
                        <p className="mt-3 text-xs text-ink-muted">
                            By signing up you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
                        </p>

                        <div className="mt-8 border-t border-black/10 pt-6 hidden md:block">
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ on every device</div>
                            <p className="text-sm text-ink-soft mb-4">View CSVs free in your browser. Edit, save & sync them anywhere with Pro — 14-day free trial.</p>
                            <div className="flex gap-3 flex-wrap">
                                <span className="inline-flex items-center gap-2 px-3 py-2 border border-ink/15 bg-paper text-xs ui-heading uppercase tracking-widest text-ink-soft" data-testid="app-store-soon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                                    Coming soon: App Store
                                </span>
                                <span className="inline-flex items-center gap-2 px-3 py-2 border border-ink/15 bg-paper text-xs ui-heading uppercase tracking-widest text-ink-soft" data-testid="play-store-soon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M3.6 1.6c-.4.3-.6.7-.6 1.3v18.2c0 .6.2 1 .6 1.3l10.7-10.7L3.6 1.6zM14.5 11.7L4.4 1.5l13.4 7.6-3.3 2.6zM4.4 22.5l10.1-10.2 3.3 2.6-13.4 7.6zM21 13.4c.7-.4 1-1 1-1.4s-.3-1-1-1.4l-2.4-1.4-3.5 2.8 3.5 2.8 2.4-1.4z"/></svg>
                                    Coming soon: Google Play
                                </span>
                            </div>
                            <p className="mt-4 text-xs text-ink-muted">Want to be first to know? Sign up now and we'll email you the moment they launch.</p>
                        </div>
                    </div>
                </div>
                <div className="hidden md:flex flex-col justify-between p-12 bg-cobalt text-white order-1 md:order-2">
                    <div>
                        <h1 className="font-display font-black tracking-tighter text-5xl leading-[0.95]">
                            Free<br />to start.
                        </h1>
                        <p className="mt-5 text-white/80 max-w-sm">Open and view any CSV — no card, no commitment. Upgrade to Pro when you need to edit, save, or run the export presets.</p>
                    </div>
                    <ul className="text-sm text-white/80 space-y-2">
                        <li>· Free viewer on every device</li>
                        <li>· 14-day Pro trial, no card up front</li>
                        <li>· Cancel anytime · 30-day money-back</li>
                    </ul>
                </div>
            </section>
        </>
    );
}
